import React from "react";
import { motion } from "framer-motion";
import { Eye, Lock, Database, UserPlus, Bell, Info } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

export default function PrivacyPage() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const sections = [
    {
      title: "Data Collection",
      content: "We collect information you provide directly to us, such as when you create an account, post an item, or communicate with other users. This includes your name, email, and phone number.",
      icon: UserPlus
    },
    {
      title: "How We Use Data",
      content: "Your information helps us provide, maintain, and improve our services, process transactions, and send you technical notices and support messages.",
      icon: Database
    },
    {
      title: "Information Sharing",
      content: "We do not share your personal information with third parties except as described in this policy, such as to comply with legal obligations or protect our rights.",
      icon: Eye
    },
    {
      title: "Data Security",
      content: "We use industry-standard security measures to protect your information from unauthorized access, disclosure, or alteration.",
      icon: Lock
    },
    {
      title: "Cookies & Tracking",
      content: "We use cookies and similar technologies to track activity on our service and hold certain information to improve your browsing experience.",
      icon: Info
    },
    {
      title: "Your Rights",
      content: "You have the right to access, update, or delete your personal information at any time through your account settings or by contacting us.",
      icon: Bell
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden bg-gray-50">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-gray-900 via-gray-900 to-green-900/30 -z-10"></div>
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.h1 
              {...fadeIn}
              className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-400"
            >
              Privacy Policy
            </motion.h1>
            <motion.p 
              {...fadeIn}
              transition={{ delay: 0.2 }}
              className="text-lg text-gray-400 max-w-2xl mx-auto"
            >
              Your privacy is our priority. Learn how we handle your data 
              with transparency and security.
            </motion.p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-24 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {sections.map((section, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-8 rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                  <section.icon className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h2>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {section.content}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            {...fadeIn}
            className="mt-24 p-10 rounded-[3rem] bg-gray-900 text-white relative overflow-hidden"
          >
            <div className="absolute -bottom-12 -right-12 w-64 h-64 bg-green-500/10 rounded-full blur-3xl"></div>
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold mb-6">Have concerns about your privacy?</h2>
              <p className="text-gray-400 mb-8 text-lg">
                We are committed to resolving any complaints about our collection 
                or use of your personal data. Reach out to our privacy officer.
              </p>
              <a 
                href="mailto:privacy@huluorder.com" 
                className="inline-flex items-center justify-center px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-2xl transition-all shadow-lg shadow-green-900/20"
              >
                Contact Privacy Officer
              </a>
            </div>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
